import { getHeaders } from "../request-headers.ts";
import type {
  CreateNewsCommentRequest,
  GetNewsCommentsParams,
  GetNewsListParams,
  GetNewsReactionsRequest,
  LatestNewsHighlightResponse,
  NewsCommentItem,
  NewsDetailResponse,
  NewsListResponse,
  NewsReactionItem,
  ProxyApexNewsImageParams,
  ToggleNewsReactionRequest,
  ToggleNewsReactionResponse,
} from "../../model";

import { cnapexApiFetch } from "../../../fetcher.ts";

export const getProxyApexNewsImageUrl = (params?: ProxyApexNewsImageParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/apex-news-image?${stringifiedParams}`
    : `/api/apex-news-image`;
};

export const proxyApexNewsImage = async (
  params?: ProxyApexNewsImageParams,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<void> => {
  return cnapexApiFetch<void>(getProxyApexNewsImageUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getGetNewsListUrl = (params?: GetNewsListParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/news?${stringifiedParams}` : `/api/news`;
};

export const getNewsList = async (
  params?: GetNewsListParams,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<NewsListResponse> => {
  return cnapexApiFetch<NewsListResponse>(getGetNewsListUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getGetNewsCommentsUrl = (params: GetNewsCommentsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/news/comments?${stringifiedParams}`
    : `/api/news/comments`;
};

export const getNewsComments = async (
  params: GetNewsCommentsParams,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<NewsCommentItem[]> => {
  return cnapexApiFetch<NewsCommentItem[]>(getGetNewsCommentsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getCreateNewsCommentUrl = () => {
  return `/api/news/comments`;
};

export const createNewsComment = async (
  createNewsCommentRequest: CreateNewsCommentRequest,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<NewsCommentItem> => {
  return cnapexApiFetch<NewsCommentItem>(getCreateNewsCommentUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(createNewsCommentRequest),
  });
};

export const getGetLatestNewsHighlightUrl = () => {
  return `/api/news/highlight`;
};

export const getLatestNewsHighlight = async (
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<LatestNewsHighlightResponse> => {
  return cnapexApiFetch<LatestNewsHighlightResponse>(getGetLatestNewsHighlightUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetNewsReactionsUrl = () => {
  return `/api/news/reactions/query`;
};

export const getNewsReactions = async (
  getNewsReactionsRequest: GetNewsReactionsRequest,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<NewsReactionItem[]> => {
  return cnapexApiFetch<NewsReactionItem[]>(getGetNewsReactionsUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(getNewsReactionsRequest),
  });
};

export const getToggleNewsReactionUrl = () => {
  return `/api/news/reactions/toggle`;
};

export const toggleNewsReaction = async (
  toggleNewsReactionRequest: ToggleNewsReactionRequest,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<ToggleNewsReactionResponse> => {
  return cnapexApiFetch<ToggleNewsReactionResponse>(getToggleNewsReactionUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(toggleNewsReactionRequest),
  });
};

export const getGetNewsDetailUrl = (slug: string) => {
  return `/api/news/${slug}`;
};

export const getNewsDetail = async (
  slug: string,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<NewsDetailResponse> => {
  return cnapexApiFetch<NewsDetailResponse>(getGetNewsDetailUrl(slug), {
    ...options,
    method: "GET",
  });
};
